import { useEffect, useRef, useState } from 'react';

export interface MenuItem {
  label: string;
  shortcut?: string;
  onClick?: () => void;
  separator?: boolean;
  disabled?: boolean;
  danger?: boolean;
}

export interface MenuDef {
  id: string;
  label: string;
  items: MenuItem[];
}

interface Props {
  menus: MenuDef[];
  /**
   * When true, render the menu buttons inline (no wrapper background/border/height)
   * so they can sit alongside other elements in a parent flex row.
   */
  inline?: boolean;
}

export function MenuBar({ menus, inline = false }: Props) {
  const [open, setOpen] = useState<string | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!barRef.current?.contains(e.target as Node)) setOpen(null);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  const wrapperStyle: React.CSSProperties = inline
    ? {
        display: 'flex',
        alignItems: 'stretch',
        height: 28,
        fontSize: 12,
        userSelect: 'none',
      }
    : {
        display: 'flex',
        alignItems: 'stretch',
        height: 28,
        background: 'var(--panel)',
        borderBottom: '1px solid var(--panel-border)',
        fontSize: 12,
        userSelect: 'none',
      };

  return (
    <div ref={barRef} style={wrapperStyle}>
      {menus.map((menu) => (
        <MenuDropdown
          key={menu.id}
          menu={menu}
          isOpen={open === menu.id}
          setOpen={(next) => setOpen(next ? menu.id : null)}
          transferOpen={(id) => open && setOpen(id)}
        />
      ))}
    </div>
  );
}

function MenuDropdown({
  menu,
  isOpen,
  setOpen,
  transferOpen,
}: {
  menu: MenuDef;
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  transferOpen: (id: string) => void;
}) {
  return (
    <div style={{ position: 'relative', height: '100%' }}>
      <button
        onClick={() => setOpen(!isOpen)}
        onMouseEnter={() => transferOpen(menu.id)}
        style={{
          background: isOpen ? 'var(--accent-soft)' : 'transparent',
          color: isOpen ? 'var(--accent)' : 'var(--text)',
          border: 'none',
          padding: '0 12px',
          height: '100%',
          fontSize: 12,
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        {menu.label}
      </button>
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            minWidth: 240,
            background: 'var(--panel)',
            border: '1px solid var(--panel-border)',
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 100,
            padding: '4px 0',
          }}
        >
          {menu.items.map((item, i) => {
            if (item.separator) {
              return (
                <div
                  key={i}
                  style={{
                    height: 1,
                    background: 'var(--panel-border)',
                    margin: '4px 0',
                  }}
                />
              );
            }
            return (
              <button
                key={i}
                disabled={item.disabled}
                onClick={() => {
                  if (item.onClick) item.onClick();
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  width: '100%',
                  padding: '6px 14px',
                  background: 'transparent',
                  border: 'none',
                  color: item.danger ? 'var(--danger)' : 'var(--text)',
                  opacity: item.disabled ? 0.4 : 1,
                  cursor: item.disabled ? 'default' : 'pointer',
                  fontSize: 12,
                  textAlign: 'left',
                  alignItems: 'center',
                  gap: 12,
                }}
                onMouseEnter={(e) => {
                  if (!item.disabled) e.currentTarget.style.background = 'var(--accent-soft)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <span style={{ flex: 1 }}>{item.label}</span>
                {item.shortcut && (
                  <span
                    style={{
                      fontSize: 10,
                      color: 'var(--text-dim)',
                      fontFamily: 'ui-monospace, Consolas, monospace',
                      fontWeight: 500,
                    }}
                  >
                    {item.shortcut}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
